import { useState } from 'react';
import { ErrorText } from '../../../components/common/Form';
import Button from '../../../components/common/Button';
import ImageUploader from '../../../components/common/Form/ImageUploader';
import MultipleMediaUploader from '../../../components/MultipleMediaUploader';
import ProfileHeroMediaEditor from '../../../components/ProfileHeroMediaEditor';
import { AlbumMediaInterface } from '../../../types/AlbumMediaInterface';
import { BlobWrapper } from '../../../types/MediaInterface';

/* eslint-disable no-unused-vars -- callback params document handler signatures only */
interface RestaurantPhotoPageProps {
  thumbnailImage: Blob | string | null;
  profileImages: (BlobWrapper | AlbumMediaInterface)[];
  menuCoverImage: Blob | string | null;
  galleryImages: (BlobWrapper | AlbumMediaInterface)[];
  handleThumbnailImageUpload: (file: Blob) => void;
  handleProfileImagesUpload: (files: (BlobWrapper | AlbumMediaInterface)[]) => void;
  handleMenuCoverImageUpload: (file: Blob) => void;
  handleGalleryImagesUpload: (files: (BlobWrapper | AlbumMediaInterface)[]) => void;
  onNext: () => void;
  onBack: () => void;
}
/* eslint-enable no-unused-vars */

const MAX_PROFILE_MEDIA = 10;

const RestaurantPhotoPage = ({
  thumbnailImage,
  profileImages,
  menuCoverImage,
  galleryImages,
  handleThumbnailImageUpload,
  handleProfileImagesUpload,
  handleMenuCoverImageUpload,
  handleGalleryImagesUpload,
  onNext,
  onBack
}: RestaurantPhotoPageProps) => {
  const [errorsMap, setErrorsMap] = useState<Map<string, string>>(new Map());

  const clearError = (key: string) =>
    setErrorsMap((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });

  const handleNextClicked = () => {
    const newErrors = new Map<string, string>();

    if (!thumbnailImage) {
      newErrors.set('thumbnailImage', 'Restaurant Thumbnail photo is required.');
    }
    if (!profileImages?.length) {
      newErrors.set('profileImages', 'At least one restaurant profile photo or video is required.');
    } else if (profileImages.length > MAX_PROFILE_MEDIA) {
      newErrors.set('profileImages', `Maximum of ${MAX_PROFILE_MEDIA} profile media items allowed.`);
    }
    if (!menuCoverImage) {
      newErrors.set('menuCoverImage', 'Restaurant Menu Cover photo is required.');
    }

    setErrorsMap(newErrors);
    if (newErrors.size === 0) onNext();
  };

  return (
    <div className="restaurant-photo-page">
      <div className="restaurant-wizard-thumbnail-image-container">
        <h2 className="restaurant-wizard-section-heading">RESTAURANT SEARCH PHOTO</h2>
        <p className="restaurant-wizard-image-copy">
          Choose your favorite restaurant photo for your thumbnail photo. This photo will displayed in TapTab&apos;s
          restaurant list. This photo should be at least 100 px wide & 100 px tall and will be cropped to fit a 4:6
          aspect ratio.
        </p>
        <ImageUploader
          id="1"
          image={thumbnailImage}
          onUpdate={(file: Blob) => {
            handleThumbnailImageUpload(file);
            clearError('thumbnailImage');
          }}
        />
        {errorsMap.has('thumbnailImage') && <ErrorText error={errorsMap.get('thumbnailImage') ?? ''} />}
      </div>
      <div className="restaurant-wizard-profile-image-container">
        <ProfileHeroMediaEditor
          variant="uploader"
          label="RESTAURANT PROFILE PHOTOS"
          description="Choose up to 10 photos or video for your profile hero carousel. These appear at the top of your restaurant profile. Each photo should be at least 100 px wide and 100 px tall."
          media={profileImages}
          onUpdate={(files) => {
            handleProfileImagesUpload(files);
            clearError('profileImages');
          }}
          maxItems={MAX_PROFILE_MEDIA}
        />
        {errorsMap.has('profileImages') && <ErrorText error={errorsMap.get('profileImages') ?? ''} />}
      </div>
      <div className="restaurant-wizard-menuCover-image-container">
        <h2 className="restaurant-wizard-section-heading">MENU PHOTO</h2>
        <p className="restaurant-wizard-image-copy">
          Choose a photo that will appear at the top of your menu. The photo should be at least 100 px wide and 100 px
          tall.
        </p>
        <ImageUploader
          id="3"
          image={menuCoverImage}
          onUpdate={(file: Blob) => {
            handleMenuCoverImageUpload(file);
            clearError('menuCoverImage');
          }}
        />
        {errorsMap.has('menuCoverImage') && <ErrorText error={errorsMap.get('menuCoverImage') ?? ''} />}
      </div>
      <div className="restaurant-wizard-gallery-image-container">
        <h2 className="restaurant-wizard-section-heading">GALLERY PHOTOS</h2>
        <p className="restaurant-wizard-image-copy">
          Choose up to 10 photos for your TapTab profile. Each photo should be at least 100 px wide and 100 px tall.
        </p>
        <MultipleMediaUploader media={galleryImages} onUpdate={handleGalleryImagesUpload} type="image" maxImages={10} />
      </div>
      <div className="restaurant-wizard-photo-buttons">
        <Button className="restaurant-wizard-back-button" onClick={onBack}>
          BACK
        </Button>
        <Button className="restaurant-wizard-next-button" onClick={handleNextClicked}>
          NEXT
        </Button>
      </div>
    </div>
  );
};

export default RestaurantPhotoPage;
